import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EMPTY, Subject, switchMap, take } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import {
  TellentAccessService,
  TellentManageRedirectService,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  PositionChangeApplyService,
  TellentPositionsConflictsService,
} from '@core/tellent-positions-logic';
import {
  AssignMembersOptions,
  ConfirmPositionChangeMember,
  TellentPosition,
  TellentPositionMember,
} from '@core/tellent-positions-types';
import { PositionDetailsMemberItemComponent } from '@core/tellent-positions-ui';
import { TellentDevFlag } from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  ButtonComponent,
  EmptyStateComponent,
  IconComponent,
  InputComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

import { PositionsDialogsService } from '../../../services/positions-dialogs.service';

@Component({
  selector: 'tlc-position-details-employees',
  templateUrl: 'position-details-employees.component.html',
  styleUrls: ['position-details-employees.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    EmptyStateComponent,
    TranslatePipe,
    InputComponent,
    PositionDetailsMemberItemComponent,
  ],
  providers: [TellentManageRedirectService],
})
export class PositionDetailsEmployeesComponent {
  private readonly positionsDialogService = inject(PositionsDialogsService);
  private readonly positionChangeApplyService = inject(PositionChangeApplyService);
  private readonly positionsConflictsService = inject(TellentPositionsConflictsService);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly notificationsController = inject(NotificationsController);
  private readonly t = injectTranslate();
  private readonly redirectService = inject(TellentManageRedirectService);

  private readonly searchQuery$ = new Subject<string>();

  public readonly hasPositionsHistoryFlag = injectDevFlagSignal(TellentDevFlag.OnePositionsHistory);

  public readonly canManageStructure = inject(TellentAccessService).canManageStructure;

  public readonly positionData = input.required<TellentPosition | null>();

  public assignedMembers = computed<TellentPositionMember[]>(
    () => this.positionData()?.members || [],
  );
  public hasMembersAssigned = computed(() => this.assignedMembers().length > 0);

  public filteredMembers = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.assignedMembers();
    return this.assignedMembers().filter(m =>
      resolveMemberFullName(m).toLowerCase().includes(query),
    );
  });

  public searchQuery = toSignal(this.searchQuery$.pipe(debounceTime(300)), { initialValue: '' });

  public onSearchChange(value: string): void {
    this.searchQuery$.next(value);
  }

  public assignMembers(): void {
    this.positionsDialogService
      .openAssignMembersDialog(this.positionData()!)
      .pipe(
        take(1),
        switchMap(result => {
          if (!result?.membersGuids?.length) return EMPTY;
          return this.confirmAssignment(result.membersGuids);
        }),
      )
      .subscribe(outcome => {
        if (outcome.status === 'cancelled') return;

        if (outcome.status === 'success') {
          this.notificationsController.success(
            this.t(
              'tlc.positions.position_details_dialog.tabs.employees.assign_members_dialog.notification_success',
            ),
          );
          return;
        }

        this.notificationsController.error(
          outcome.errorMessage ??
            this.t('tlc.positions.confirm_position_change_dialog.notification_error'),
        );
      });
  }

  public unassignMember(member: TellentPositionMember): void {
    this.positionsDialogService
      .openUnassignMemberDialog(this.positionData()!, member)
      .pipe(take(1))
      .subscribe();
  }

  public viewMemberProfile(memberGuid: string): void {
    this.redirectService.openEmployeeProfile(memberGuid);
  }

  public viewMemberHistory(memberGuid: string): void {
    this.positionsDialogService
      .openPositionHistoryDialog({ memberGuid, canEdit: this.canManageStructure() })
      .pipe(take(1))
      .subscribe();
  }

  private confirmAssignment(membersGuids: string[]) {
    if (this.hasPositionsHistoryFlag()) {
      return this.confirmPositionChange(membersGuids);
    }

    return this.confirmReassignment(membersGuids);
  }

  private confirmReassignment(membersGuids: string[]) {
    const conflicts = this.positionsConflictsService.findMembersAssignedElsewhere(
      membersGuids,
      this.positionData()!.guid,
    );

    if (!conflicts.length) {
      return this.doAssignMembers(membersGuids);
    }

    return this.positionsDialogService
      .openReassignMemberWarningDialog({
        targetPositionName: this.positionData()!.name,
        members: conflicts,
      })
      .pipe(
        take(1),
        switchMap(warningResult => {
          if (!warningResult?.confirmed) return EMPTY;
          return this.doAssignMembers(membersGuids);
        }),
      );
  }

  private confirmPositionChange(membersGuids: string[]) {
    return this.positionsDialogService
      .openConfirmPositionChangeDialog({
        targetPositionName: this.positionData()!.name,
        members: this.buildPositionChangeMembers(membersGuids),
      })
      .pipe(
        take(1),
        switchMap(result =>
          this.positionChangeApplyService.apply(this.positionData()!.guid, membersGuids, result),
        ),
      );
  }

  private buildPositionChangeMembers(membersGuids: string[]): ConfirmPositionChangeMember[] {
    const conflicts = this.positionsConflictsService.findMembersAssignedElsewhere(
      membersGuids,
      this.positionData()!.guid,
    );
    const currentPositionByGuid = new Map(
      conflicts.map(conflict => [conflict.member.guid, conflict.currentPosition]),
    );

    return membersGuids.map(guid => {
      const member = this.membersDataFacade.teamMemberById(guid)();
      return {
        guid,
        fullName: member ? resolveMemberFullName(member) : '',
        avatarThumbUrl: member?.avatarThumbUrl ?? null,
        currentPosition: currentPositionByGuid.get(guid) ?? null,
      };
    });
  }

  private doAssignMembers(membersGuids: string[], options?: AssignMembersOptions) {
    return this.positionChangeApplyService.assign(this.positionData()!.guid, membersGuids, options);
  }
}
